import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { minBy } from 'es-toolkit/compat';
import { scan } from 'rxjs';

import { isNotNil, ProcessingMediaStatus } from '@malou-io/package-utils';

import { MalouSpinnerComponent } from '#core/components/spinner/spinner/malou-spinner.component';
import { ProcessingMediasService } from '#core/services/processing-medias.service';
import { GalleryImportMediaService } from '#modules/gallery/services/gallery-import-media.service';
import { GalleryImportMediaStore } from '#modules/gallery/stores/gallery-import-media.store';
import { MediaApiService, UploadV2Result } from '#modules/media/services/media.api.service';
import { CircleProgressComponent } from '#shared/components/circle-progress/circle-progress.component';
import { MAX_CONCURRENT_UPLOADS, MAX_INFLIGHT_UPLOAD_BYTES } from '#shared/constants/media-upload';
import { BodyDragAndDropEventsService } from '#shared/services/body-drag-and-drop-events.service';

enum ImportStatus {
    SCHEDULED = 'SCHEDULED',
    UPLOADING = 'UPLOADING',
    PROCESSING = 'PROCESSING',
    DONE = 'DONE',
}

type ImportJob = { file: File } & ( // the file object is used as an identifier (the referenced object stays the same)
    | {
          status: ImportStatus.SCHEDULED;
          // the folder is captured when the file is scheduled: the upload may start after the user navigated elsewhere
          folderId: string | null;
      }
    | {
          status: ImportStatus.UPLOADING;
          /** a number between 0 and 1 */
          progress: number | null;
      }
    | { status: ImportStatus.PROCESSING; processingMediaId: string | null; progress: number | null }
    | { status: ImportStatus.DONE }
);

// How long the completed (100%) state stays visible before the card is cleared
const CLEAR_DELAY_MS = 1000;

const getImportProgress = (job: ImportJob): number => {
    switch (job.status) {
        case ImportStatus.SCHEDULED:
            return 0;
        case ImportStatus.UPLOADING:
            return (job.progress ?? 0) / 2;
        case ImportStatus.PROCESSING:
            return 0.6;
        case ImportStatus.DONE:
            return 1;
    }
};

/**
 * This component is supposed to be instanciated once for the whole application.
 *
 * See https://airtable.com/appIqBldyX7wZlWnp/tblbOxMTpexQyxSTV/viwVSdtBlz857nQiA/recdHmIJwdJGtRTaf
 */
@Component({
    selector: 'app-gallery-import-media-v2',
    templateUrl: './gallery-import-media-v2.component.html',
    imports: [CircleProgressComponent, MalouSpinnerComponent, TranslateModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GalleryImportMediaV2Component implements OnDestroy, OnInit {
    private _fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');

    private readonly _bodyDragAndDropEventsService = inject(BodyDragAndDropEventsService);
    private readonly _galleryImportMediaService = inject(GalleryImportMediaService);
    private readonly _mediaService = inject(MediaApiService);
    private readonly _translateService = inject(TranslateService);
    private readonly _processingMediasService = inject(ProcessingMediasService);
    private readonly _galleryImportMediaStore = inject(GalleryImportMediaStore);

    private readonly _errors = this._galleryImportMediaStore.filesErrors;
    private readonly _dragAndDropEnabled = this._galleryImportMediaStore.isGalleryOpen;
    readonly jobs = signal<ImportJob[]>([]);
    private readonly _currentFolderId = this._galleryImportMediaStore.currentFolderId;

    /** Number of files still being imported (DONE jobs excluded so the count decreases as files complete). */
    readonly importingFilesCount = computed(() => this.jobs().filter((job) => job.status !== ImportStatus.DONE).length);

    /** True during the brief completion flash: jobs are still displayed but every one of them is DONE. */
    readonly areAllJobsDone = computed(() => {
        const jobs = this.jobs();
        return jobs.length > 0 && jobs.every((job) => job.status === ImportStatus.DONE);
    });

    /**
     * Raw aggregate progress (between 0 and 100)
     */
    private readonly _rawImportProgress = computed<number>(() => {
        const jobs = this.jobs();
        if (!jobs.length) {
            return 0;
        }
        const sum = jobs.reduce((sum_, j) => sum_ + getImportProgress(j), 0);
        return (sum / jobs.length) * 100;
    });

    /**
     * Displayed progress, clamped so the circle never moves backward within a single import session.
     * Adding files mid-import (or finishing a wave then starting another) would otherwise make the
     * circle jump back. It resets to 0 once everything is cleared (raw === 0 ⇒ no active jobs).
     */
    readonly importProgress$ = toObservable(this._rawImportProgress).pipe(
        scan((displayed, raw) => (raw === 0 ? 0 : Math.max(displayed, raw)), 0)
    );

    readonly ImportStatus = ImportStatus;

    constructor() {
        this._bodyDragAndDropEventsService.drop.pipe(takeUntilDestroyed()).subscribe(this._onDrop);
        this._bodyDragAndDropEventsService.dragOver.pipe(takeUntilDestroyed()).subscribe(this._onDragOver);
    }

    ngOnInit(): void {
        this._galleryImportMediaService.element.set(this);
    }

    ngOnDestroy(): void {
        this._galleryImportMediaService.element.set(null);
    }

    /** Can be called from other components */
    public openFilePicker(): void {
        this._fileInput().nativeElement.value = '';
        this._fileInput().nativeElement.click();
    }

    onFileInputChange(_event: Event): void {
        const files: File[] = Array.from(this._fileInput().nativeElement.files ?? []);
        for (const file of files) {
            this._scheduleImport(file);
        }
    }

    private _onDragOver = (event: DragEvent): void => {
        if (this._dragAndDropEnabled()) {
            event.preventDefault();
        }
    };

    private _onDrop = (event: DragEvent): void => {
        if (!this._dragAndDropEnabled()) {
            return;
        }

        event.preventDefault();
        const droppedFiles: FileList | null = event.dataTransfer?.files as FileList | null;
        if (droppedFiles) {
            for (const file of Array.from(droppedFiles)) {
                this._scheduleImport(file);
            }
        }
    };

    /**
     * Appends a file to the list of file to import. This does not mean that the file
     * will be uploaded immediately.
     */
    private _scheduleImport(file: File): void {
        const importJob: ImportJob = { file, status: ImportStatus.SCHEDULED, folderId: this._currentFolderId() };
        this.jobs.update((jobs) => [...jobs, importJob]);
        this._maybeStartUpload();
    }

    /** Starts a scheduled import job if possible. Does nothing otherwise. */
    private _maybeStartUpload(): void {
        setTimeout(() => this._maybeStartUploadImpl());
    }

    /** Should not be called directly. Call it via _maybeStartUpload(). */
    private _maybeStartUploadImpl(): void {
        this.jobs.update((jobs: ImportJob[]): ImportJob[] => {
            // Start the smallest scheduled file first: small files finish quickly (visible progress) and, paired with
            // the byte budget, a large file never blocks smaller ones that still fit in flight. Large files end last.
            const job = minBy(
                jobs.filter((j) => j.status === ImportStatus.SCHEDULED),
                (scheduledJob) => scheduledJob.file.size
            );
            if (!job) {
                // nothing to start
                return jobs;
            }

            // Processing is handled server-side by a lambda, so we only cap concurrent uploads (by total bytes in flight,
            // with a hard count cap) and let processing jobs run without blocking new uploads.
            const uploadingJobs = jobs.filter((j) => j.status === ImportStatus.UPLOADING);
            const bytesInFlight = uploadingJobs.reduce((total, j) => total + j.file.size, 0);
            const isOverBudget =
                uploadingJobs.length >= MAX_CONCURRENT_UPLOADS || bytesInFlight + job.file.size > MAX_INFLIGHT_UPLOAD_BYTES;
            // Always allow at least one upload, so a single file larger than the byte budget is never starved.
            if (uploadingJobs.length > 0 && isOverBudget) {
                return jobs;
            }

            const restaurantId = this._galleryImportMediaStore.currentRestaurantId();
            if (!restaurantId || job.status !== ImportStatus.SCHEDULED) {
                return jobs;
            }

            void this._mediaService
                .uploadV2({
                    file: job.file,
                    onProgress: (progress) => this._updateUploadProgress(job.file, progress),
                    queryParams: { restaurantId, folderId: job.folderId },
                })
                .then((result) => this._onUploadEnd(job.file, result));

            return [...jobs.filter((j) => j !== job), { ...job, status: ImportStatus.UPLOADING, progress: 0 }];
        });
    }

    private _updateUploadProgress(file: File, progress: number): void {
        this._updateJob(file, (job: ImportJob): ImportJob | null => {
            if (job.status !== ImportStatus.UPLOADING) {
                // The upload already ended; ignore late progress events (do not remove the job).
                return job;
            }

            return {
                file: job.file,
                status: ImportStatus.UPLOADING,
                progress,
            };
        });
    }

    private _onProcessingEnd(file: File, mediaId: string | null): void {
        if (mediaId) {
            this._mediaService.getMediumById(mediaId).subscribe(({ data: media }) => {
                this._galleryImportMediaStore.setCreatedMedia([media]);
            });
        } else {
            this._showProcessingError(file);
        }

        this._markJobDone(file);
        this._maybeStartUpload();
    }

    private _onUploadEnd(file: File, result: UploadV2Result): void {
        if (!result.success) {
            this._showUploadError(file);
            this._markJobDone(file);
            this._maybeStartUpload();
            return;
        }

        this._updateJob(file, (job: ImportJob): ImportJob | null => {
            if (job.status !== ImportStatus.UPLOADING) {
                throw new Error();
            }
            return { file, status: ImportStatus.PROCESSING, processingMediaId: result.result.processingMediaId, progress: 0 };
        });

        void this._processingMediasService.waitUntilEnded(result.result.processingMediaId).then((processingMedia) => {
            if (processingMedia.status === ProcessingMediaStatus.SUCCESS && processingMedia.mediaId) {
                this._onProcessingEnd(file, processingMedia.mediaId);
            } else {
                this._onProcessingEnd(file, null);
            }
        });
    }

    /**
     * Marks an import as finished (success or error). The job is kept in a terminal DONE state so it keeps contributing 100% to the progress, then the whole card is cleared once every job is done.
     */
    private _markJobDone(file: File): void {
        this._updateJob(file, (job: ImportJob): ImportJob | null => ({ file: job.file, status: ImportStatus.DONE }));
        this._clearJobsIfAllDone();
    }

    private _clearJobsIfAllDone(): void {
        if (!this.jobs().length || !this.jobs().every((job) => job.status === ImportStatus.DONE)) {
            return;
        }

        // Let the user briefly see the completed (100%) state before the card disappears.
        setTimeout(() => {
            if (this.jobs().length && this.jobs().every((job) => job.status === ImportStatus.DONE)) {
                this.jobs.set([]);
            }
        }, CLEAR_DELAY_MS);
    }

    private _showUploadError(file: File): void {
        this._showImportError(file, this._translateService.instant('gallery.upload_errors.network_error'));
    }

    private _showProcessingError(file: File): void {
        this._showImportError(file, this._translateService.instant('gallery.upload_errors.invalid_file'));
    }

    private _showImportError(file: File, reason: string): void {
        this._galleryImportMediaStore.setFilesErrors([
            ...this._errors(),
            this._translateService.instant('gallery.upload_error', { fileName: file.name, reason }),
        ]);
    }

    /** The job is removed if the updateFn fuction returns null */
    private _updateJob(file: File, updateFn: (job: ImportJob) => ImportJob | null): void {
        this.jobs.update((jobs) =>
            jobs
                .map((job) => {
                    if (job.file === file) {
                        const newJob = updateFn(job);
                        if (newJob && newJob.file !== file) {
                            throw new Error('File mismatch');
                        }
                        return newJob;
                    }
                    return job;
                })
                .filter(isNotNil)
        );
    }
}
